// src/pages/tournamentDetails/components/PayoutsCard.js
import React, { useMemo } from "react";
import { placeSuffix, formatMoney } from "../helpers";

const PRESET_PAYOUTS = {
  1: [100],
  2: [65, 35],
  3: [50, 30, 20],
  4: [40, 30, 20, 10],
  5: [35, 25, 18, 12, 10],
  6: [30, 22, 17, 12, 10, 9],
  7: [27, 20, 16, 12, 10, 8, 7],
  8: [24, 18, 15, 12, 10, 8, 7, 6],
  9: [22, 17, 14, 12, 10, 8, 7, 5, 5],
};

function getSuggestedPaidPlaces(entrantsCount) {
  const entrants = Math.max(0, Number(entrantsCount || 0));

  if (entrants <= 1) return 1;
  if (entrants <= 6) return 2;
  if (entrants <= 12) return 3;
  if (entrants <= 18) return 4;
  if (entrants <= 27) return 5;
  if (entrants <= 36) return 6;
  if (entrants <= 45) return 7;
  if (entrants <= 54) return 8;
  return 9;
}

function getPresetPercents(places) {
  const n = Math.max(1, Math.min(9, Number(places || 1)));
  return PRESET_PAYOUTS[n] || PRESET_PAYOUTS[1];
}

function buildPresetPayouts(places) {
  const percents = getPresetPercents(places);
  return percents.map((percent, idx) => ({
    place: idx + 1,
    percent,
  }));
}

function BreakdownRow({ label, amount, emphasize = false, accent = false }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span
        className={
          emphasize
            ? "text-zinc-100 font-semibold"
            : accent
            ? "text-blue-300"
            : "text-zinc-400"
        }
      >
        {label}
      </span>
      <span
        className={`shrink-0 text-right ${
          emphasize
            ? "text-zinc-100 font-bold"
            : accent
            ? "text-blue-200 font-semibold"
            : "text-zinc-200 font-semibold"
        }`}
      >
        {formatMoney(amount)}
      </span>
    </div>
  );
}

export default function PayoutsCard({
  tournament,

  // tournament computed values
  prizePool,
  buyIn,
  entrantsCount,
  entryPrizeContribution,
  rebuyPrizeContribution,
  bountyEnabled,
  rebuysEnabled,
  totalRebuys,
  addOnsEnabled,
  totalAddOns,
  addOnCost,

  // dealer appreciation accounting
  dealerAppreciationEnabled,
  dealerAppreciationOptIns,
  dealerAppreciationCost,
  dealerAppreciationPrizePoolPerOptIn,
  dealerAppreciationDealerPerOptIn,
  totalDealerAppreciationPrizePool,
  totalDealerAppreciationDealerPayout,

  // payouts state
  payouts,
  setPayouts,
  payoutPlaces,
  setPayoutPlaces,

  // actions
  savePayouts,
  savingPayouts,

  // permissions
  isAdmin,
  isCompleted,

  // payout calculations
  payoutAmountForPlace,
}) {
  const safeEntrantsCount = Math.max(0, Number(entrantsCount || 0));
  const maxPlaceOption = Math.max(1, Math.min(9, safeEntrantsCount || 1));
  const safeSelectedPlaces = Math.max(
    1,
    Math.min(maxPlaceOption, Number(payoutPlaces || 1))
  );

  const safeBuyIn = Math.max(0, Number(buyIn || 0));
  const safeEntryPrizeContribution = Math.max(
    0,
    Number(entryPrizeContribution ?? safeBuyIn)
  );
  const safeRebuyPrizeContribution = Math.max(0, Number(rebuyPrizeContribution ?? safeEntryPrizeContribution));
  const safeTotalRebuys = Math.max(0, Number(totalRebuys || 0));
  const safeTotalAddOns = Math.max(0, Number(totalAddOns || 0));
  const safeAddOnCost = Math.max(0, Number(addOnCost || 0));

  const safeDealerOptIns = Math.max(0, Number(dealerAppreciationOptIns || 0));
  const safeDealerCost = Math.max(0, Number(dealerAppreciationCost || 0));
  const safeDealerPrizePoolPerOptIn = Math.max(
    0,
    Number(dealerAppreciationPrizePoolPerOptIn || 0)
  );
  const safeDealerDealerPerOptIn = Math.max(
    0,
    Number(dealerAppreciationDealerPerOptIn || 0)
  );
  const safeDealerPrizePoolTotal = Math.max(
    0,
    Number(totalDealerAppreciationPrizePool || 0)
  );
  const safeDealerPayoutTotal = Math.max(
    0,
    Number(totalDealerAppreciationDealerPayout || 0)
  );

  const entryTotal = safeEntrantsCount * safeEntryPrizeContribution;
  const rebuyTotal = safeTotalRebuys * safeRebuyPrizeContribution;
  const addOnTotal = safeTotalAddOns * safeAddOnCost;

  const accountedPrizePool =
    entryTotal + rebuyTotal + addOnTotal + safeDealerPrizePoolTotal;
  const prizePoolAdjustment = Number(prizePool || 0) - accountedPrizePool;
  const showPrizePoolAdjustment = Math.abs(prizePoolAdjustment) >= 0.01;

  const dealerAppreciationCollected =
    safeDealerPrizePoolTotal + safeDealerPayoutTotal;

  const showDealerAppreciation =
    dealerAppreciationEnabled === true ||
    safeDealerOptIns > 0 ||
    dealerAppreciationCollected > 0;

  const suggestedPlaces = useMemo(() => {
    return Math.min(maxPlaceOption, getSuggestedPaidPlaces(safeEntrantsCount));
  }, [maxPlaceOption, safeEntrantsCount]);

  const totalPercent = useMemo(() => {
    return (Array.isArray(payouts) ? payouts : []).reduce(
      (sum, p) => sum + Number(p.percent || 0),
      0
    );
  }, [payouts]);

  const currentPayoutsAreSuggested = useMemo(() => {
    const suggested = buildPresetPayouts(suggestedPlaces);
    const current = Array.isArray(payouts)
      ? payouts
          .slice()
          .sort((a, b) => Number(a.place || 0) - Number(b.place || 0))
          .map((p) => ({
            place: Number(p.place || 0),
            percent: Number(p.percent || 0),
          }))
      : [];

    if (current.length !== suggested.length) return false;

    return current.every((p, idx) => {
      return (
        p.place === suggested[idx].place && p.percent === suggested[idx].percent
      );
    });
  }, [payouts, suggestedPlaces]);

  const applySuggestedStructure = () => {
    setPayoutPlaces(suggestedPlaces);
    setPayouts(buildPresetPayouts(suggestedPlaces));
  };

  const applySelectedStructure = () => {
    setPayouts(buildPresetPayouts(safeSelectedPlaces));
  };

  const payoutOptions = Array.from({ length: maxPlaceOption }, (_, idx) => idx + 1);

  return (
    <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-semibold text-[#B87333] mb-2">Prize Pool</h3>

          <p className="text-zinc-200">
            Prize Pool: <span className="font-bold">{formatMoney(prizePool)}</span>
          </p>

          <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-4">
            <p className="text-zinc-100 font-semibold">Prize Pool Breakdown</p>
            <p className="text-zinc-500 text-xs mt-1 mb-3">
              Shows exactly where the regular payout pool comes from.
            </p>

            <BreakdownRow
              label={`${bountyEnabled ? "Entries (prize pool)" : "Entries"}: ${safeEntrantsCount} × ${formatMoney(
                safeEntryPrizeContribution
              )}`}
              amount={entryTotal}
            />

            {rebuysEnabled || safeTotalRebuys > 0 ? (
              <BreakdownRow
                label={`Rebuys: ${safeTotalRebuys} × ${formatMoney(
                  safeRebuyPrizeContribution
                )} to prize pool`}
                amount={rebuyTotal}
              />
            ) : null}

            {addOnsEnabled || safeTotalAddOns > 0 ? (
              <BreakdownRow
                label={`Add-ons: ${safeTotalAddOns} × ${formatMoney(safeAddOnCost)}`}
                amount={addOnTotal}
              />
            ) : null}

            {showDealerAppreciation ? (
              <BreakdownRow
                label={`Dealer Appreciation: ${safeDealerOptIns} × ${formatMoney(
                  safeDealerPrizePoolPerOptIn
                )} to prize pool`}
                amount={safeDealerPrizePoolTotal}
                accent
              />
            ) : null}

            {showPrizePoolAdjustment ? (
              <BreakdownRow
                label="Other / manual prize-pool adjustment"
                amount={prizePoolAdjustment}
              />
            ) : null}

            <div className="border-t border-zinc-800 mt-3 pt-2">
              <BreakdownRow
                label="Total Prize Pool"
                amount={prizePool}
                emphasize
              />
            </div>
          </div>

          {showDealerAppreciation ? (
            <div className="mt-3 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 mb-2">
                <div>
                  <p className="text-zinc-100 font-semibold">Dealer Appreciation</p>
                  <p className="text-zinc-500 text-xs mt-1">
                    Kept separate so the dealer portion is never included in tournament payouts.
                  </p>
                </div>
                <div className="text-left sm:text-right mt-1 sm:mt-0">
                  <p className="text-blue-200 font-semibold">
                    {formatMoney(dealerAppreciationCollected)} collected
                  </p>
                  <p className="text-zinc-500 text-xs">
                    {safeDealerOptIns} player{safeDealerOptIns === 1 ? "" : "s"} × {formatMoney(safeDealerCost)}
                  </p>
                </div>
              </div>

              <BreakdownRow
                label={`To prize pool (${formatMoney(safeDealerPrizePoolPerOptIn)} each)`}
                amount={safeDealerPrizePoolTotal}
                accent
              />
              <BreakdownRow
                label={`To dealers (${formatMoney(safeDealerDealerPerOptIn)} each)`}
                amount={safeDealerPayoutTotal}
              />
            </div>
          ) : null}

          <p className="text-zinc-400 text-sm mt-4">
            Suggested based on {safeEntrantsCount} entrant
            {safeEntrantsCount === 1 ? "" : "s"}: {" "}
            <span className="text-zinc-100 font-semibold">
              Pay Top {suggestedPlaces}
            </span>
          </p>

          {Array.isArray(payouts) && payouts.length > 0 ? (
            <p className="text-zinc-500 text-xs mt-2">
              Payouts rounded to nearest {" "}
              {Number(tournament?.payoutRoundingIncrement || 5) === 10
                ? "$10"
                : "$5"}
              .
            </p>
          ) : null}
        </div>

        {isAdmin && (
          <button
            onClick={savePayouts}
            disabled={savingPayouts}
            className={`px-4 py-2 rounded font-semibold shrink-0 ${
              savingPayouts
                ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                : "bg-green-700 hover:bg-green-800 text-white"
            }`}
          >
            {savingPayouts ? "Saving..." : "Save Payouts"}
          </button>
        )}
      </div>

      {isAdmin && !isCompleted && (
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p className="text-zinc-100 font-semibold">Suggested payout structure</p>
                <p className="text-zinc-400 text-sm mt-1">
                  Based on the current field size, a good default is to pay {" "}
                  <span className="text-zinc-100 font-semibold">
                    Top {suggestedPlaces}
                  </span>
                  .
                </p>
                <p className="text-zinc-500 text-xs mt-2">
                  You can still override this and choose a different number of paid places.
                </p>
              </div>

              <button
                onClick={applySuggestedStructure}
                className="px-4 py-3 rounded font-semibold bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
              >
                {currentPayoutsAreSuggested
                  ? `Suggested Structure Applied`
                  : `Apply Suggested Top ${suggestedPlaces}`}
              </button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3 items-end">
            <div>
              <label className="block text-zinc-300 text-sm mb-1">Pay Places</label>
              <select
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={safeSelectedPlaces}
                onChange={(e) => setPayoutPlaces(Number(e.target.value))}
              >
                {payoutOptions.map((n) => (
                  <option key={n} value={n}>
                    Top {n}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={applySelectedStructure}
              className="px-4 py-3 rounded font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700"
            >
              Apply Top {safeSelectedPlaces} Preset
            </button>

            <div className="text-right">
              <p
                className={`text-sm ${
                  Math.round(totalPercent) === 100 ? "text-green-400" : "text-red-400"
                }`}
              >
                Total: {totalPercent}% (must be 100%)
              </p>
            </div>
          </div>
        </div>
      )}

      {!Array.isArray(payouts) || payouts.length === 0 ? (
        <p className="text-zinc-400 mt-4">No payouts set yet.</p>
      ) : (
        <div className="mt-5 space-y-2">
          {payouts
            .slice()
            .sort((a, b) => a.place - b.place)
            .map((p) => {
              const amount = payoutAmountForPlace(p.place);

              return (
                <div
                  key={p.place}
                  className="flex items-center justify-between gap-3 p-3 rounded border border-zinc-800 bg-zinc-950"
                >
                  <div className="text-zinc-100 font-semibold">
                    {p.place}
                    {placeSuffix(p.place)} Place
                  </div>

                  <div className="flex items-center gap-3">
                    {isAdmin && !isCompleted ? (
                      <>
                        <input
                          type="number"
                          className="w-24 p-2 bg-zinc-800 text-zinc-100 rounded text-right"
                          value={p.percent}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setPayouts((prev) =>
                              (Array.isArray(prev) ? prev : []).map((x) =>
                                x.place === p.place ? { ...x, percent: v } : x
                              )
                            );
                          }}
                        />
                        <span className="text-zinc-400">%</span>
                      </>
                    ) : (
                      <span className="text-zinc-300">{p.percent}%</span>
                    )}

                    <span className="text-zinc-100 font-bold w-32 text-right">
                      {formatMoney(amount)}
                    </span>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
